import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

// Import Firebase to make it globally available for debugging
import './config/firebase';

import App from './App';
import './styles/globals.css';

// Initialize Sentry for error monitoring
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      new BrowserTracing(),
    ],
    tracesSampleRate: 0.1,
    beforeSend: (event) => {
      // Filter out some common errors in production
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes('Network Error') || 
            error?.value?.includes('Loading chunk')) {
          return null;
        }
      }
      return event;
    },
  });
}

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      retry: (failureCount: number, error: any) => {
        // Don't retry auth errors
        if (error?.status === 401 || error?.status === 403) {
          return false;
        }
        // Retry up to 2 times for other errors
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Error boundary for unhandled errors
const ErrorBoundary = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="text-red-600 text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
        <p className="text-gray-600 mb-6">
          {error?.message || 'An unexpected error occurred. Please try refreshing the page.'}
        </p>
        <div className="space-x-4">
          <button
            onClick={resetError}
            className="btn btn-primary"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-secondary"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  ),
  beforeCapture: (scope) => {
    scope.setTag('component', 'App');
    scope.setLevel('error');
  },
});

// Global error handling
window.addEventListener('error', (event) => {
  console.error('Global error:', event.error);
  
  // Send to Sentry if available
  if (window.Sentry) {
    window.Sentry.captureException(event.error);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  
  // Send to Sentry if available  
  if (window.Sentry) {
    window.Sentry.captureException(event.reason);
  }
  
  // Prevent the default console error
  event.preventDefault();
});

// Get root element
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Create root and render
const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary />
      </BrowserRouter>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);
